import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive, ArchiveRestore, ExternalLink, Loader2, Lock, Pencil, Plus, Search, Sparkles, StickyNote, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KbBackButton, KbToolbarButton } from "@/components/KbToolbarButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { audienceSummary } from "@/lib/kbAudiences";
import { useAuth } from "@/context/AuthContext";

const PAGE = 50;
const STATE_STYLE = {
  in_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  changes_requested: "bg-rose-100 text-rose-700",
};

export default function KBManageArticles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("kb-manage.q", "");
  const [status, setStatus] = usePersistedState("kb-manage.status", "all");
  const [type, setType] = usePersistedState("kb-manage.type", "all");
  const [sort, setSort] = usePersistedState("kb-manage.sort", "name");
  const [cats, setCats] = usePersistedState("kb-manage.cats", []);
  const [tree, setTree] = useState([]);
  const [busy, setBusy] = useState("");
  const [archived, setArchived] = usePersistedState("kb-manage.archived", "live");   // live | only | all
  const [canDelete, setCanDelete] = useState(false);
  const [picked, setPicked] = useState([]);

  useEffect(() => { api.get("/kb/tree").then((r) => setTree(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/kb/manage/articles", { params: {
      q, skip, limit: PAGE, sort, archived,
      status: status === "all" ? "" : status,
      content_type: type === "all" ? "" : type,
      category_ids: cats.join(","),
    } })
      .then((r) => {
        setRows(r.data.articles || []);
        setTotal(r.data.total || 0);
        setCanDelete(!!r.data.can_delete);
        setPicked([]);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [q, skip, sort, status, type, cats, archived]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const catName = useMemo(() => Object.fromEntries(tree.map((c) => [c.cat_id, c.name.trim()])), [tree]);

  const act = async (row, fn, msg) => {
    setBusy(row.slug);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  // Publishing goes through the reviewer — the list can only take an article
  // back off the KB. A draft's pill opens the editor to submit it for review;
  // a super admin may publish it on the spot.
  const togglePublish = (row) => {
    if (row.is_draft) {
      if (!isAdmin) { navigate(`/kb/a/${row.slug}/edit`); return; }
      if (!window.confirm(`Publish “${row.name}” now? As a super admin you skip the reviewer${row.reviewer_name ? ` (${row.reviewer_name})` : ""} — it goes live immediately.`)) return;
      act(row, () => api.patch(`/kb/articles/${row.slug}`, { status: "published" }), "Published — it's live now");
      return;
    }
    if (!window.confirm(`Unpublish “${row.name}”? It disappears for readers until a reviewer approves it again.`)) return;
    act(row, () => api.patch(`/kb/articles/${row.slug}`, { status: "draft" }), "Unpublished — hidden from readers");
  };

  const move = (row, catId) => act(row,
    () => api.patch(`/kb/articles/${row.slug}`, { primary_category_id: catId, category_ids: [catId] }),
    "Category updated");

  const remove = async (row) => {
    const { data: back } = await api.get(`/kb/articles/${row.slug}/backlinks`).catch(() => ({ data: { count: 0 } }));
    const warn = back.count
      ? `\n\n${back.count} other article${back.count === 1 ? "" : "s"} link${back.count === 1 ? "s" : ""} here — those links will break permanently.`
      : "";
    if (!window.confirm(`Permanently delete “${row.name}”? This can't be undone.${warn}`)) return;
    act(row, () => api.delete(`/kb/articles/${row.slug}`), "Article deleted");
  };

  const toggleArchive = async (row) => {
    const going = !row.archived;
    if (going) {
      const { data: back } = await api.get(`/kb/articles/${row.slug}/backlinks`).catch(() => ({ data: { count: 0 } }));
      const warn = back.count
        ? `\n\n${back.count} other article${back.count === 1 ? "" : "s"} link${back.count === 1 ? "s" : ""} here — readers following those links will hit a page they can't see.`
        : "";
      if (!window.confirm(
        `Archive “${row.name}”? It disappears from the Knowledge Base for readers but stays here so you can restore it.${warn}`)) return;
    }
    act(row, () => api.post(`/kb/articles/${row.slug}/archive`, { archived: going }),
      going ? "Archived" : "Restored");
  };

  const bulk = async (kind) => {
    if (!picked.length) return;
    const verb = { archive: "Archive", restore: "Restore", delete: "Permanently delete" }[kind];
    if (!window.confirm(`${verb} ${picked.length} article${picked.length === 1 ? "" : "s"}?`)) return;
    setBusy("bulk");
    try {
      if (kind === "delete") await api.post("/kb/articles/bulk-delete", { slugs: picked });
      else await api.post("/kb/articles/bulk-archive", { slugs: picked, archived: kind === "archive" });
      toast.success(`${verb}d ${picked.length} article${picked.length === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const allPicked = rows.length > 0 && picked.length === rows.length;

  return (
    <div data-testid="kb-manage-articles-page">
      <PageHeader title="Manage articles" testid="kb-manage-articles-header"
        subtitle={`${total} article${total === 1 ? "" : "s"} — including drafts and snippets`}>
        <KbBackButton />
        <KbToolbarButton primary icon={Plus} testid="kb-manage-new-article"
          onClick={() => navigate("/kb/new")}>New article</KbToolbarButton>
        <KbToolbarButton icon={StickyNote} testid="kb-manage-new-snippet"
          onClick={() => navigate("/kb/new?snippet=1")}>New snippet</KbToolbarButton>
        <KbToolbarButton icon={Sparkles} testid="kb-manage-studio"
          onClick={() => navigate("/kb/studio")}>Compose</KbToolbarButton>
      </PageHeader>

      <Card className="border-slate-200 dark:border-slate-800 p-4">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input data-testid="kb-manage-search" value={q} placeholder="Search title, slug or tag…"
              onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="pl-8" />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
            <SelectTrigger className="w-[150px]" data-testid="kb-manage-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="in_review">In review</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={(v) => { setType(v); setSkip(0); }}>
            <SelectTrigger className="w-[140px]" data-testid="kb-manage-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="article">Articles</SelectItem>
              <SelectItem value="snippet">Snippets</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[150px]" data-testid="kb-manage-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: title</SelectItem>
              <SelectItem value="recent">Sort: recently edited</SelectItem>
              <SelectItem value="views">Sort: most viewed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={archived} onValueChange={(v) => { setArchived(v); setSkip(0); }}>
            <SelectTrigger className="w-[160px]" data-testid="kb-manage-archived"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="live">Live only</SelectItem>
              <SelectItem value="only">Archived only</SelectItem>
              <SelectItem value="all">Live + archived</SelectItem>
            </SelectContent>
          </Select>
          <div className="w-[200px]">
            <KbCategoryFilter tree={tree} value={cats} testid="kb-manage-cat"
              onChange={(ids) => { setCats(ids); setSkip(0); }} />
          </div>
        </div>

        {picked.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
            data-testid="kb-manage-bulk-bar">
            <span className="font-semibold text-slate-700 dark:text-slate-200">{picked.length} selected</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy === "bulk"}
              data-testid="kb-manage-bulk-archive" onClick={() => bulk("archive")}>
              <Archive className="mr-1 h-3.5 w-3.5" /> Archive
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy === "bulk"}
              data-testid="kb-manage-bulk-restore" onClick={() => bulk("restore")}>
              <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore
            </Button>
            {canDelete && (
              <Button variant="outline" size="sm" className="h-7 border-rose-200 text-xs text-rose-600 hover:bg-rose-50"
                disabled={busy === "bulk"} data-testid="kb-manage-bulk-delete" onClick={() => bulk("delete")}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete permanently
              </Button>
            )}
            <button type="button" className="ml-auto text-slate-500 dark:text-slate-400 hover:text-slate-700"
              data-testid="kb-manage-bulk-clear" onClick={() => setPicked([])}>Clear selection</button>
          </div>
        )}

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-manage-empty">No articles match those filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  <th className="w-8 py-2 pr-2">
                    <Checkbox checked={allPicked} data-testid="kb-manage-select-all"
                      onCheckedChange={(v) => setPicked(v ? rows.map((r) => r.slug) : [])} />
                  </th>
                  <th className="py-2 pr-3 font-semibold">Title</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold">Category</th>
                  <th className="py-2 pr-3 font-semibold">Visible to</th>
                  <th className="py-2 pr-3 font-semibold">Last edited</th>
                  <th className="py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.article_id} data-testid={`kb-manage-row-${r.slug}`}
                    className={`border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/60 ${r.archived ? "bg-slate-50/80" : ""}`}>
                    <td className="py-2.5 pr-2">
                      <Checkbox checked={picked.includes(r.slug)} data-testid={`kb-manage-pick-${r.slug}`}
                        onCheckedChange={(v) => setPicked((p) => (v ? [...p, r.slug] : p.filter((s) => s !== r.slug)))} />
                    </td>
                    <td className="py-2.5 pr-3">
                      <button onClick={() => navigate(`/kb/a/${r.slug}/edit`)}
                        data-testid={`kb-manage-title-${r.slug}`}
                        className="text-left font-medium text-slate-800 dark:text-slate-100 hover:text-primary">
                        {r.name}
                      </button>
                      {r.archived && (
                        <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300"
                          data-testid={`kb-manage-archived-${r.slug}`}>Archived</span>
                      )}
                      {r.is_snippet && <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">Snippet</span>}
                      {(r.tags || []).length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {r.tags.slice(0, 4).map((t) => (
                            <span key={t} className="rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] text-slate-500 dark:text-slate-400">{t}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <button onClick={() => togglePublish(r)} disabled={busy === r.slug}
                        data-testid={`kb-manage-toggle-${r.slug}`}
                        title={r.is_draft ? (isAdmin ? "Super admin: click to publish now" : "Drafts go live through their reviewer — open the editor to submit it") : "Click to unpublish"}
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          r.is_draft ? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                                     : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
                        {r.is_draft ? "Draft" : "Published"}
                      </button>
                      {r.has_pending && (
                        <span className="ml-1.5 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700"
                          title={`A newer version${r.pending_by ? ` by ${r.pending_by}` : ""} is waiting for review — readers still see the published one`}
                          data-testid={`kb-manage-pending-${r.slug}`}>
                          Update pending
                        </span>
                      )}
                      {r.publish_at && (
                        <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                          data-testid={`kb-manage-scheduled-${r.slug}`}>
                          Scheduled {new Date(r.publish_at).toLocaleDateString("en-CA", { timeZone: "America/Toronto", month: "short", day: "numeric" })}
                        </span>
                      )}
                      {r.review_state !== "none" && (
                        <span className={`ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATE_STYLE[r.review_state] || ""}`}>
                          {r.review_state === "in_review" ? `Review: ${r.reviewer_name || "—"}`
                            : r.review_state === "approved" ? "Approved" : "Changes requested"}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Select value={r.primary_category_id || ""} onValueChange={(v) => move(r, v)}>
                        <SelectTrigger className="h-7 w-[190px] text-xs" data-testid={`kb-manage-cat-${r.slug}`}>
                          <SelectValue placeholder="Uncategorised">
                            {catName[r.primary_category_id] || "Uncategorised"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {tree.map((c) => (
                            <SelectItem key={c.cat_id} value={c.cat_id} className="text-xs">
                              {"— ".repeat(Math.max(0, (c.depth || 1) - 1))}{c.name.trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">
                      {audienceSummary(r.audiences)}
                      {(r.target_teams?.length || r.target_orgs?.length) ? (
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-indigo-600"
                             data-testid={`kb-manage-narrowed-${r.slug}`}
                             title="Narrowed — hidden in the reader from everyone outside this list">
                          <Lock className="h-3 w-3" />
                          {r.target_teams?.length ? r.target_teams.join(", ")
                            : `${r.target_orgs.length} organization${r.target_orgs.length > 1 ? "s" : ""}`}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">
                      {r.edited_at ? new Date(r.edited_at).toLocaleDateString() : "—"}
                      {r.edited_by ? <div className="text-slate-400 dark:text-slate-500">{r.edited_by}</div> : null}
                    </td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="Open editor"
                          data-testid={`kb-manage-edit-${r.slug}`} onClick={() => navigate(`/kb/a/${r.slug}/edit`)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="View article"
                          data-testid={`kb-manage-view-${r.slug}`} onClick={() => navigate(`/kb/a/${r.slug}`)}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2"
                          title={r.archived ? "Restore to the Knowledge Base" : "Archive — hides it from readers"}
                          data-testid={`kb-manage-archive-${r.slug}`}
                          disabled={busy === r.slug} onClick={() => toggleArchive(r)}>
                          {r.archived ? <ArchiveRestore className="h-3.5 w-3.5 text-emerald-600" />
                            : <Archive className="h-3.5 w-3.5" />}
                        </Button>
                        {canDelete && r.archived && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-500 hover:bg-rose-50"
                            title="Delete permanently" data-testid={`kb-manage-delete-${r.slug}`}
                            disabled={busy === r.slug} onClick={() => remove(r)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE && (
          <div className="mt-4 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>Showing {skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="kb-manage-prev"
                disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>Previous</Button>
              <Button variant="outline" size="sm" data-testid="kb-manage-next"
                disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
